/**
 * @fileoverview Reporting phase view (MOR-556, Phase B)
 *
 * Manage and generate reports on a job. The user picks a firm report template,
 * Morgan's app-orchestrated multi-agent flow drafts each section from the
 * already-extracted case data, and the user reviews / edits / exports the
 * assembled report.
 */

import {
  createElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createRoot, type Root } from "react-dom/client";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useAction } from "convex/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import DOMPurify from "dompurify";
import { api } from "~/convex/_generated/api";
import type { Id } from "~/convex/_generated/dataModel";
import { Badge, Button, Spinner } from "@/ui/primitives";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/ui/composed/overlays/alert-dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/ui/composed/overlays/sheet";
import {
  AlertCircle,
  ArrowLeft,
  BookText,
  Check,
  CircleDot,
  Download,
  FileText,
  History,
  ListTree,
  Loader2,
  MessageSquare,
  SquareDashedMousePointer,
  PanelLeftClose,
  PanelRightClose,
  PanelRightOpen,
  Paperclip,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcw,
  ScrollText,
  Sparkles,
  Trash2,
  User,
  Wrench,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { extractErrorMessage } from "@/utils/errors";
import { cn } from "@/utils/misc";
import { allSectionsDrafted } from "~/convex/jobs/reporting/assemble";
import {
  buildCanvasExportBlob,
  canvasExportFileName,
  CANVAS_EXPORT_MIME,
  type CanvasExportDestination,
  type CanvasExportFormat,
} from "@/utils/canvas/canvasExport";
import { renderCanvasBlocksForPrint } from "@/utils/canvas/canvasToPdfBlob";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/ui/composed/menus/dropdown-menu";
import { diffHtml } from "@/utils/precedents/htmlDiff";
import { ReportAttachmentsPanel } from "./ReportAttachmentsPanel";
import {
  bundleReportWithAnnexures,
  type ExportAnnexure,
} from "./reportAttachments";
import { Logo } from "@/ui/layout/logo";
import { ChartRenderer } from "@/components/agents/universal/chartRenderer";
import { WorkflowStatusSelect } from "@/components/tasks/WorkflowStatusSelect";
import { chartSpecFromAttr } from "@/components/agents/universal/canvas/chartHtml";
import {
  ChatInput,
  ChatMessage,
  type ChatComposerAttachment,
  type ChatMentionItem,
  type ChatMessageData,
} from "@/ui/composed/chat";
import {
  JobChatDocumentPicker,
  type ChatDocPick,
} from "@/components/agents/universal/ChatDocumentPicker";
import { DocumentDetail } from "@/components/jobs/phases/gather/documents/DocumentDetail";
import { PodClaimOverlay } from "@/components/jobs/phases/recoveries/PodClaimOverlay";
import {
  CitationLayer,
  ReportEditor,
  ReportRenderer,
  ReportSourcesList,
} from "./ReportEditor";
import { ReportCitationContext } from "./reportCitationContext";
import { hasUnfilledPlaceholder } from "./reportUnfilled";
import {
  markCitationsInDom,
  parseCitedSources,
  stripCitations,
  buildBibliographyHtml,
  buildCitationNumbers,
  renderCitationNumbersForExport,
} from "./reportSources";
import {
  ReportElementPicker,
  SELECTED_ELEMENT_CLASS,
} from "./ReportElementPicker";
import {
  buildSelection,
  selectionChipLabel,
  toWireSelection,
  type ReportSelection,
} from "./reportSelection";
import {
  FigureImpactPanel,
  type FigureImpactResult,
  type FigureImpactScan,
} from "./FigureImpactPanel";

type ReportStatus = "draft" | "generating" | "review" | "complete" | "failed";

const CHART_DIV_RE =
  /<div\s+data-morgan-chart=(['"])[\s\S]*?\1\s*>(?:\s*<\/div>)?/gi;

type ExportFormat = CanvasExportFormat;
type ExportDestination = CanvasExportDestination;

/**
 * Redline diff that keeps chart blocks intact. A character-level HTML diff
 * shreds a chart's encoded attribute, so we swap each chart for a stable token
 * (content-keyed, so an unchanged chart stays unchanged) before diffing, then
 * restore the chart divs. The diff wraps an added/removed chart token in
 * <ins>/<del>, so the restored chart shows as added/removed.
 */
function buildChartAwareDiff(current: string, proposed: string): string {
  const tokenByChart = new Map<string, string>();
  const chartByToken = new Map<string, string>();
  const tokenize = (html: string) =>
    html.replace(CHART_DIV_RE, (m) => {
      const existing = tokenByChart.get(m);
      if (existing) return ` ${existing} `;
      const tok = `MORGANCHARTTOKEN${tokenByChart.size}`;
      tokenByChart.set(m, tok);
      chartByToken.set(tok, m);
      return ` ${tok} `;
    });
  const diff = diffHtml(tokenize(current), tokenize(proposed));
  return diff.replace(
    /MORGANCHARTTOKEN\d+/g,
    (tok) => chartByToken.get(tok) ?? "",
  );
}

export function ReportingView({ jobId }: { jobId: Id<"jobs"> }) {
  // The selected report lives in the URL (?report=…) so the sidebar's
  // Reporting group can deep-link to a specific report (MOR-917).
  const search = useSearch({ strict: false }) as { report?: string };
  const navigate = useNavigate();
  const selectedId = (search.report ?? null) as Id<"jobReports"> | null;
  const setSelectedId = useCallback(
    (id: Id<"jobReports"> | null) => {
      void navigate({
        to: "/dashboard/jobs/$jobId/reporting",
        params: { jobId },
        search: id ? { report: id } : {},
      });
    },
    [navigate, jobId],
  );
  const [newOpen, setNewOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    _id: Id<"jobReports">;
    title: string;
  } | null>(null);

  const { data: reports, isLoading } = useQuery(
    convexQuery(api.jobs.reporting.queries.listReports, { jobId }),
  );
  const { mutateAsync: deleteReport } = useMutation({
    mutationFn: useConvexMutation(api.jobs.reporting.mutations.deleteReport),
  });

  const rows = reports ?? [];
  const selectedTitle =
    rows.find((r) => r._id === selectedId)?.title ?? "This report";

  return (
    <>
      {selectedId ? (
        <ReportDetail
          reportId={selectedId}
          jobId={jobId}
          onBack={() => setSelectedId(null)}
          onRequestDelete={() =>
            setDeleteTarget({ _id: selectedId, title: selectedTitle })
          }
        />
      ) : (
        <div className="mx-auto w-full max-w-5xl p-6">
          <div className="rounded-xl border bg-background p-5 shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h1 className="text-lg font-semibold">Reports</h1>
                <p className="mt-1 text-sm text-primary/60">
                  Generate reports from your firm's templates. Morgan will draft
                  each section from this job's data for you to review, edit and
                  export.
                </p>
              </div>
              <Button size="sm" onClick={() => setNewOpen(true)}>
                <Plus className="mr-1 size-4" />
                New report
              </Button>
            </div>

            {isLoading ? (
              <div className="flex justify-center py-12">
                <Spinner />
              </div>
            ) : rows.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed py-12 text-muted-foreground">
                <FileText className="size-8" />
                <p className="text-sm">No reports yet.</p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setNewOpen(true)}
                >
                  <Plus className="mr-1 size-4" />
                  New report
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {rows.map((r) => (
                  <div
                    key={r._id}
                    className="group flex items-center gap-1 rounded-lg border bg-background pr-2 hover:bg-muted/40"
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedId(r._id)}
                      className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3 text-left"
                    >
                      <ScrollText className="size-4 shrink-0 text-muted-foreground" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">
                          {r.title}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {r.sectionTotal} section
                          {r.sectionTotal !== 1 ? "s" : ""}
                        </p>
                      </div>
                      <ReportStatusBadge
                        status={r.status}
                        done={r.sectionDone}
                        total={r.sectionTotal}
                      />
                    </button>
                    <button
                      type="button"
                      aria-label={`Delete ${r.title}`}
                      className="shrink-0 rounded-md p-1.5 text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-red-600 focus-visible:opacity-100 group-hover:opacity-100"
                      onClick={() =>
                        setDeleteTarget({ _id: r._id, title: r.title })
                      }
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <NewReportDialog
            jobId={jobId}
            open={newOpen}
            onOpenChange={setNewOpen}
            onCreated={(id) => {
              setNewOpen(false);
              setSelectedId(id);
            }}
          />
        </div>
      )}

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete report?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.title}" will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(event) => {
                // Radix closes the dialog unless default is prevented, which
                // clears `deleteTarget` before the mutation runs.
                event.preventDefault();
                const target = deleteTarget;
                if (!target) return;
                void deleteReport({ reportId: target._id })
                  .then(() => {
                    if (selectedId === target._id) setSelectedId(null);
                    toast.success("Report deleted");
                  })
                  .catch((err: unknown) => {
                    toast.error(extractErrorMessage(err, "Failed to delete"));
                  })
                  .finally(() => setDeleteTarget(null));
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function NewReportDialog({
  jobId,
  open,
  onOpenChange,
  onCreated,
}: {
  jobId: Id<"jobs">;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (id: Id<"jobReports">) => void;
}) {
  const [picked, setPicked] = useState<Id<"reportTemplates"> | null>(null);
  const [creating, setCreating] = useState(false);
  // MOR-926 — steering notes + tagged reference documents.
  const [steeringText, setSteeringText] = useState("");
  const [attachedDocs, setAttachedDocs] = useState<ChatDocPick[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  // "New report" starts from scratch. Cancelling used to leave the previous
  // pick behind, so reopening looked like a template had been chosen for you —
  // and the guidance field, which only shows once one is picked, came with it.
  useEffect(() => {
    if (!open) return;
    setPicked(null);
    setSteeringText("");
    setAttachedDocs([]);
  }, [open]);

  const { data: templates, isLoading } = useQuery({
    ...convexQuery(api.jobs.reporting.queries.listTemplatesForJob, { jobId }),
    enabled: open,
  });
  const { data: jobDocs } = useQuery({
    ...convexQuery(api.jobs.gatherInformation.documents.queries.listDocuments, {
      jobId,
    }),
    enabled: open,
  });
  const { mutateAsync: createReport } = useMutation({
    mutationFn: useConvexMutation(api.jobs.reporting.mutations.createReport),
  });

  const mentionItems: ChatMentionItem[] = useMemo(
    () => (jobDocs ?? []).map((d) => ({ id: d._id, name: d.fileName })),
    [jobDocs],
  );
  const composerAttachments: ChatComposerAttachment[] = attachedDocs.map(
    (d) => ({ id: d.documentId, name: d.fileName }),
  );
  const addDocs = useCallback((picks: ChatDocPick[]) => {
    setAttachedDocs((prev) => {
      const have = new Set(prev.map((d) => d.documentId));
      return [...prev, ...picks.filter((p) => !have.has(p.documentId))].slice(
        0,
        10,
      );
    });
  }, []);

  const rows = templates ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-2xl"
        // Focus the dialog rather than the first template, whose focus ring
        // otherwise reads as a selection. Focus stays inside the dialog, so
        // tabbing and Escape behave as usual.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          (e.currentTarget as HTMLElement).focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>New report</DialogTitle>
          <DialogDescription>
            Choose a report template to generate from.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
            No report templates yet. Create one in Settings → Report Templates
            first.
          </div>
        ) : (
          <div className="flex max-h-60 flex-col gap-1.5 overflow-auto">
            {rows.map((t) => {
              const ready =
                t.extractionStatus === "completed" && t.sectionCount > 0;
              return (
                <button
                  key={t._id}
                  type="button"
                  disabled={!ready}
                  onClick={() => setPicked(t._id)}
                  className={cn(
                    "flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm",
                    picked === t._id
                      ? "border-primary bg-primary/5"
                      : "border-border hover:bg-muted",
                    !ready && "cursor-not-allowed opacity-50",
                  )}
                >
                  <ScrollText className="size-4 shrink-0 text-muted-foreground" />
                  <span className="flex-1 truncate">{t.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {ready ? `${t.sectionCount} sections` : "Not ready"}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* MOR-926 — steer the generation: extra context in the practitioner's
            own words, with @-mentions / the picker to tag reference files.
            Held back until a template is picked: shown alongside the list it
            read as part of the furniture and went unnoticed, where appearing
            on selection makes it the obvious next step. */}
        {picked !== null && (
          <div className="border-t pt-4 duration-200 animate-in fade-in slide-in-from-bottom-1">
            <p className="mb-1.5 text-sm font-medium">
              Add instructions to report generator{" "}
              <span className="font-normal text-muted-foreground">
                (optional)
              </span>
            </p>
            <p className="mb-3.5 text-xs text-muted-foreground">
              Add instructions, additional context or files that should be
              considered by the report generator. You can tag files using the @
              to add them as reference material.
            </p>
            <ChatInput
              variant="field"
              className="p-0"
              maxHeightPx={260}
              onSend={() => {}}
              onValueChange={setSteeringText}
              placeholder="Add instructions or information"
              attachments={composerAttachments}
              onRemoveAttachment={(id) =>
                setAttachedDocs((prev) =>
                  prev.filter((d) => d.documentId !== id),
                )
              }
              onAttachClick={() => setPickerOpen(true)}
              mentionItems={mentionItems}
              onMentionSelect={(item) => {
                const doc = (jobDocs ?? []).find((d) => d._id === item.id);
                if (doc) {
                  addDocs([
                    {
                      documentId: doc._id,
                      fileName: doc.fileName,
                      jobId,
                    },
                  ]);
                }
              }}
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!picked || creating}
            onClick={async () => {
              if (!picked) return;
              setCreating(true);
              try {
                const id = await createReport({
                  jobId,
                  templateId: picked,
                  setupInstructions: steeringText.trim() || undefined,
                  setupDocumentIds:
                    attachedDocs.length > 0
                      ? attachedDocs.map((d) => d.documentId)
                      : undefined,
                });
                setPicked(null);
                setSteeringText("");
                setAttachedDocs([]);
                onCreated(id);
              } catch (err) {
                toast.error(
                  extractErrorMessage(err, "Failed to create report"),
                );
              } finally {
                setCreating(false);
              }
            }}
          >
            {creating && <Loader2 className="mr-1 size-4 animate-spin" />}
            Create
          </Button>
        </DialogFooter>

        <JobChatDocumentPicker
          jobId={jobId}
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onSelect={(picks) => {
            addDocs(picks);
            setPickerOpen(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

interface SectionRow {
  _id: Id<"reportSections">;
  order: number;
  parentSectionId?: Id<"reportSections">;
  title: string;
  fillMode: "generate" | "attachment";
  status: "pending" | "writing" | "written" | "reviewed";
  /** V1 attachments — the uploaded annexure file for an attachment section. */
  attachmentFileName?: string;
  attachmentFileType?: string;
  attachmentFileSize?: number;
  attachmentUrl?: string | null;
}

function ReportDetail({
  reportId,
  jobId,
  onBack,
  onRequestDelete,
}: {
  reportId: Id<"jobReports">;
  jobId: Id<"jobs">;
  onBack: () => void;
  onRequestDelete: () => void;
}) {
  const { data: report, isLoading } = useQuery(
    convexQuery(api.jobs.reporting.queries.getReport, { reportId }),
  );
  const { data: currentUser } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );
  const { mutateAsync: startGeneration } = useMutation({
    mutationFn: useConvexMutation(api.jobs.reporting.mutations.startGeneration),
  });
  const { mutateAsync: commitDraftedReport } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.reporting.mutations.commitDraftedReport,
    ),
  });
  const commitDraftedReportRef = useRef(commitDraftedReport);
  commitDraftedReportRef.current = commitDraftedReport;
  const { mutateAsync: updateReportHtml } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.reporting.mutations.updateReportHtml,
    ),
  });
  const generateUploadUrl = useConvexMutation(
    api.jobs.gatherInformation.documents.mutations.generateUploadUrl,
  );
  const saveCanvasAsDocument = useConvexMutation(
    api.agents.universal.canvas.saveCanvasAsDocument,
  );
  // MOR-866 — Word export in the firm template's own styling.
  const exportReportStyled = useAction(
    api.jobs.reporting.docxExport.actions.exportReportStyled,
  );
  const { mutateAsync: applyProposal } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.reporting.mutations.applyReportProposal,
    ),
  });
  const { mutateAsync: rejectProposal } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.reporting.mutations.rejectReportProposal,
    ),
  });
  // Figure-impact scan: flagged after a hand edit moves a figure.
  const { data: impactScan } = useQuery(
    convexQuery(api.jobs.reporting.queries.getFigureImpactScan, { reportId }),
  );
  const { mutateAsync: dismissImpactScan } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.reporting.mutations.dismissFigureImpactScan,
    ),
  });

  const [editing, setEditing] = useState(false);
  const [editedHtml, setEditedHtml] = useState("");
  // Export progress: client-side stages are set locally; during the server
  // phase of a styled export the report subscription supplies the real stage
  // (report.styledExportProgress, written by the export action).
  const [exportStage, setExportStage] = useState<
    "preparing" | "server" | "downloading" | "saving" | null
  >(null);
  const exporting = exportStage !== null;
  const serverExportStage = report?.styledExportProgress?.stage;
  const exportStageLabel =
    exportStage === "preparing"
      ? "Preparing content…"
      : exportStage === "server"
        ? serverExportStage === "analysing_template"
          ? "Analysing template…"
          : serverExportStage === "building"
            ? "Building document…"
            : "Exporting…"
        : exportStage === "downloading"
          ? "Downloading…"
          : exportStage === "saving"
            ? "Saving…"
            : null;
  const [chatOpen, setChatOpen] = useState(true);
  const [chatWide, setChatWide] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [previewDocId, setPreviewDocId] = useState<Id<"documents"> | null>(
    null,
  );
  // A cited POD claim opened in-place from the report, so the reader keeps
  // their place instead of being sent to the Recoveries phase (MOR).
  const [recordClaimId, setRecordClaimId] = useState<Id<"podClaims"> | null>(
    null,
  );
  const [sourceMode, setSourceMode] = useState<"all" | "bibliography" | "none">(
    "all",
  );
  const [nameDraft, setNameDraft] = useState("");
  // MOR-917 — the section the user last @-tagged in the chat composer,
  // highlighted (and scrolled to) in the rendered report.
  const [taggedSection, setTaggedSection] = useState<string | null>(null);
  // Table of contents down the left of the report (collapsible).
  const [tocOpen, setTocOpen] = useState(true);
  const reportBodyRef = useRef<HTMLDivElement>(null);
  // Element-level targeting: pick a paragraph / row / chart for the assistant
  // to edit instead of tagging the whole section.
  const [pickMode, setPickMode] = useState(false);
  const [selections, setSelections] = useState<ReportSelection[]>([]);
  // Elements carrying the selected highlight, so it can be lifted again.
  const selectedElsRef = useRef<HTMLElement[]>([]);

  const clearSelections = useCallback(() => {
    selectedElsRef.current.forEach((el) =>
      el.classList.remove(SELECTED_ELEMENT_CLASS),
    );
    selectedElsRef.current = [];
    setSelections([]);
  }, []);

  const removeSelection = useCallback((index: number) => {
    setSelections((prev) => prev.filter((_, i) => i !== index));
    const el = selectedElsRef.current[index];
    el?.classList.remove(SELECTED_ELEMENT_CLASS);
    selectedElsRef.current = selectedElsRef.current.filter(
      (_, i) => i !== index,
    );
  }, []);

  // Picking stays on so several elements can be collected in one go; clicking
  // an already-picked element takes it back out.
  const handlePick = useCallback(
    (picked: ReportSelection, element: HTMLElement) => {
      const existing = selectedElsRef.current.indexOf(element);
      if (existing !== -1) {
        removeSelection(existing);
        return;
      }
      element.classList.add(SELECTED_ELEMENT_CLASS);
      selectedElsRef.current = [...selectedElsRef.current, element];
      setSelections((prev) => [...prev, picked]);
      setChatOpen(true);
    },
    [removeSelection],
  );

  // A re-render of the body (proposal applied, regenerated) replaces the
  // picked nodes, so drop the selection rather than point at stale elements.
  useEffect(() => {
    clearSelections();
  }, [report?.contentHtml, clearSelections]);
  const citationCtx = useMemo(
    () => ({
      onDocumentClick: (id: string) => setPreviewDocId(id as Id<"documents">),
      onPodClaimClick: (id: string) => setRecordClaimId(id as Id<"podClaims">),
      showInline: sourceMode === "all",
    }),
    [sourceMode],
  );
  const { mutateAsync: renameReport } = useMutation({
    mutationFn: useConvexMutation(api.jobs.reporting.mutations.renameReport),
  });
  const { mutateAsync: setReportStatus } = useMutation({
    mutationFn: useConvexMutation(api.jobs.reporting.mutations.setStatus),
  });
  const { mutateAsync: setReportWorkflowStatus } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.reporting.mutations.setWorkflowStatus,
    ),
  });
  const status = report?.status;
  // Memoized (Elle): keeps downstream section memos stable across renders.
  const sections = useMemo(() => report?.sections ?? [], [report?.sections]);
  const doneCount = sections.filter(
    (s) => s.status === "written" || s.status === "reviewed",
  ).length;
  const sectionsDrafted = allSectionsDrafted(sections);

  // Every section is written but the report is still "generating" — the
  // in-flight action is (or was) stuck on the old coordinator rewrite.
  // Commit the assembled body so the practitioner is not left staring at
  // "43/43 done". Retry while the mutation is still deploying.
  useEffect(() => {
    if (status !== "generating" || !sectionsDrafted) return;
    let cancelled = false;
    const attempt = () => {
      if (cancelled) return;
      void commitDraftedReportRef.current({ reportId }).catch(() => {
        // Mutation may not be deployed yet, or a stale action still owns
        // the row — keep trying until this report leaves "generating".
      });
    };
    attempt();
    const id = window.setInterval(attempt, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [status, sectionsDrafted, reportId]);

  const citedSources = useMemo(
    () => parseCitedSources(report?.contentHtml ?? ""),
    [report?.contentHtml],
  );

  // Headings present in the report body — the table of contents, and the
  // targets offered for @-tagging in the chat.
  const tocHeadings = useMemo(() => {
    const out: Array<{ text: string; level: number }> = [];
    const re = /<h([1-3])[^>]*>([\s\S]*?)<\/h\1>/gi;
    const html = report?.contentHtml ?? "";
    for (const m of html.matchAll(re)) {
      const text = m[2].replace(/<[^>]+>/g, "").trim();
      if (text && !out.some((h) => h.text === text)) {
        out.push({ text, level: Number(m[1]) });
      }
    }
    return out;
  }, [report?.contentHtml]);
  const sectionHeadings = useMemo(
    () => tocHeadings.map((h) => h.text),
    [tocHeadings],
  );

  const editingSections = report?.editingSections ?? [];

  useEffect(() => {
    if (report?.title) setNameDraft(report.title);
  }, [report?.title]);

  const commitRename = useCallback(async () => {
    if (!report) return;
    const trimmed = nameDraft.trim();
    if (!trimmed || trimmed === report.title) {
      setNameDraft(report.title);
      return;
    }
    try {
      await renameReport({ reportId, title: trimmed });
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to rename"));
      setNameDraft(report.title);
    }
  }, [report, nameDraft, renameReport, reportId]);

  const proposedHtml = report?.proposedHtml ?? null;
  const proposalDiff = useMemo(
    () =>
      proposedHtml
        ? DOMPurify.sanitize(
            buildChartAwareDiff(report?.contentHtml ?? "", proposedHtml),
            {
              ADD_ATTR: [
                "data-morgan-chart",
                "data-morgan-cite",
                "data-cite-text",
              ],
            },
          )
        : "",
    [proposedHtml, report?.contentHtml],
  );

  const [diffMode, setDiffMode] = useState<"diff" | "after">("diff");

  // Citations in the redline are numbered by their first appearance in the
  // redline itself, not against the report's bibliography: the pane shows the
  // removed text alongside the added, so a source cited only by a deleted
  // passage still needs a number of its own.
  const diffCitationNumbers = useMemo(
    () => buildCitationNumbers(parseCitedSources(proposalDiff)),
    [proposalDiff],
  );

  // When the redline diff appears: scroll the first change into view, mark up
  // its citations, and render any chart blocks (kept intact by the
  // chart-aware diff).
  const diffRef = useRef<HTMLDivElement>(null);
  const chartRootsRef = useRef<Root[]>([]);
  useEffect(() => {
    const unmount = () => {
      chartRootsRef.current.forEach((r) => r.unmount());
      chartRootsRef.current = [];
    };
    if (!proposalDiff || diffMode !== "diff") {
      unmount();
      return;
    }
    const t = setTimeout(() => {
      const container = diffRef.current;
      if (!container) return;
      container
        .querySelector("ins, del")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      markCitationsInDom(container, diffCitationNumbers);
      container
        .querySelectorAll<HTMLElement>("div[data-morgan-chart]")
        .forEach((el) => {
          const spec = chartSpecFromAttr(
            el.getAttribute("data-morgan-chart") ?? "",
          );
          if (!spec) return;
          el.innerHTML = "";
          const root = createRoot(el);
          root.render(
            createElement(
              "div",
              { className: "rounded-lg border bg-background p-3" },
              createElement(
                "p",
                { className: "mb-2 text-sm font-semibold" },
                spec.title,
              ),
              createElement(ChartRenderer, { spec }),
            ),
          );
          chartRootsRef.current.push(root);
        });
    }, 100);
    return () => {
      clearTimeout(t);
      unmount();
    };
  }, [proposalDiff, diffMode, diffCitationNumbers]);

  const startEditing = useCallback(() => {
    setEditedHtml(report?.contentHtml ?? "");
    setEditing(true);
  }, [report?.contentHtml]);

  const handleSaveEdits = useCallback(async () => {
    try {
      await updateReportHtml({ reportId, contentHtml: editedHtml });
      setEditing(false);
      toast.success("Saved");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to save"));
    }
  }, [reportId, editedHtml, updateReportHtml]);

  // ---------------------------------------------------------------------
  // Figure-impact: jump to / reconcile a flagged figure in the report.
  // ---------------------------------------------------------------------
  // Chat composer draft, seeded when the user hands a flagged figure to the
  // assistant to reconcile.
  const [reconcileDraft, setReconcileDraft] = useState<string | null>(null);

  // Locate the block a flagged figure sits in: the smallest selectable block
  // whose text contains the anchor; else the section heading as a fallback.
  const locateImpactElement = useCallback(
    (result: FigureImpactResult): HTMLElement | null => {
      const body = reportBodyRef.current;
      if (!body) return null;
      const norm = (s: string) => s.replace(/\s+/g, " ").trim().toLowerCase();
      const anchor = norm(result.anchorText);
      if (anchor) {
        const candidates = Array.from(
          body.querySelectorAll<HTMLElement>(
            "p, table, tr, li, h1, h2, h3, h4, blockquote, pre, div[data-morgan-chart]",
          ),
        );
        const match = candidates
          .filter((el) => norm(el.textContent ?? "").includes(anchor))
          .sort(
            (a, b) =>
              (a.textContent?.length ?? 0) - (b.textContent?.length ?? 0),
          )[0];
        if (match) return match;
      }
      if (result.sectionTitle) {
        const heading = Array.from(
          body.querySelectorAll<HTMLElement>("h1, h2, h3"),
        ).find(
          (h) => norm(h.textContent ?? "") === norm(result.sectionTitle ?? ""),
        );
        if (heading) return heading;
      }
      return null;
    },
    [],
  );

  const handleJumpToImpact = useCallback(
    (result: FigureImpactResult) => {
      const el = locateImpactElement(result);
      if (!el) {
        toast.info("Couldn't locate that figure in the report.");
        return;
      }
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      // Brief highlight so the eye lands on it.
      el.classList.add(SELECTED_ELEMENT_CLASS);
      window.setTimeout(
        () => el.classList.remove(SELECTED_ELEMENT_CLASS),
        2200,
      );
    },
    [locateImpactElement],
  );

  const handleReconcileImpact = useCallback(
    (result: FigureImpactResult) => {
      const body = reportBodyRef.current;
      const el = locateImpactElement(result);
      // Pre-target the figure for the assistant via the existing element-edit
      // path, so the practitioner only has to confirm the change.
      if (el && body) {
        clearSelections();
        el.classList.add(SELECTED_ELEMENT_CLASS);
        selectedElsRef.current = [el];
        setSelections([buildSelection(el, body)]);
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      setReconcileDraft(
        `I changed ${impactScan?.changeSummary ?? "a figure"}. Update this figure to reconcile with that change.`,
      );
      setChatOpen(true);
    },
    [locateImpactElement, clearSelections, impactScan?.changeSummary],
  );

  const handleReconcileAllImpact = useCallback(() => {
    if (!impactScan || impactScan.status !== "ready") return;
    const body = reportBodyRef.current;
    // Pre-select every figure we can locate so the assistant gets them all in
    // one pass; the practitioner then confirms a single reconciling edit.
    if (body) {
      clearSelections();
      const located = impactScan.results
        .map((result) => locateImpactElement(result))
        .filter((el): el is HTMLElement => el !== null);
      // De-dupe in case two results resolve to the same element.
      const unique = Array.from(new Set(located));
      unique.forEach((el) => el.classList.add(SELECTED_ELEMENT_CLASS));
      selectedElsRef.current = unique;
      setSelections(unique.map((el) => buildSelection(el, body)));
      unique[0]?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    setReconcileDraft(
      `I changed ${impactScan.changeSummary ?? "a figure"}. Review every figure I've selected and update any that need to reconcile with that change.`,
    );
    setChatOpen(true);
  }, [impactScan, clearSelections, locateImpactElement]);

  const handleDismissImpact = useCallback(() => {
    void dismissImpactScan({ reportId }).catch(() => {});
  }, [dismissImpactScan, reportId]);

  // Surface a new figure-impact scan in the assistant: open the chat once per
  // scan when it has figures to review, so the practitioner sees the list.
  const surfacedScanId = useRef<string | null>(null);
  useEffect(() => {
    if (
      impactScan &&
      impactScan.status === "ready" &&
      impactScan.results.length > 0 &&
      surfacedScanId.current !== impactScan._id
    ) {
      surfacedScanId.current = impactScan._id;
      setChatOpen(true);
    }
  }, [impactScan]);

  const handleExport = useCallback(
    async (format: ExportFormat, destination: ExportDestination) => {
      if (!report?.contentHtml) return;
      setExportStage("preparing");
      try {
        const title = report.title || "Report";
        // Apply the source-display mode: drop inline chips unless "all"; append a
        // bibliography unless "none".
        const bodyForSourceMode =
          sourceMode !== "all"
            ? stripCitations(report.contentHtml)
            : // Footnote-style superscripts keyed to the numbered bibliography —
              // the app's inline chips mean nothing in a PDF or Word file.
              renderCitationNumbersForExport(
                report.contentHtml,
                buildCitationNumbers(citedSources),
              );
        const exportBody =
          sourceMode !== "none"
            ? bodyForSourceMode + buildBibliographyHtml(citedSources)
            : bodyForSourceMode;
        const html = DOMPurify.sanitize(exportBody, {
          ADD_ATTR: [
            "data-morgan-chart",
            "data-morgan-cite",
            "data-cite-text",
            "data-morgan-signature",
          ],
        });
        const preparerName = currentUser
          ? [currentUser.firstName, currentUser.lastName]
              .filter(Boolean)
              .join(" ") || currentUser.email
          : null;
        const footer = [
          preparerName ? `Prepared by ${preparerName}` : null,
          `Generated by Morgan · ${new Date().toLocaleString("en-AU")}`,
        ]
          .filter(Boolean)
          .join(" · ");

        const blob = await buildCanvasExportBlob({
          html,
          title,
          footer,
          format,
        });
        const fileName = canvasExportFileName(title, format);
        const mimeType = CANVAS_EXPORT_MIME[format];

        if (destination === "download") {
          setExportStage("downloading");
          const { downloadBlob } =
            await import("@/utils/export/exportHtmlDocument");
          // V1 attachments: bundle any uploaded annexure files alongside the
          // report as a zip so the export actually includes them.
          const annexures: ExportAnnexure[] = [];
          for (const s of sections) {
            if (
              s.fillMode === "attachment" &&
              s.attachmentUrl &&
              s.attachmentFileName
            ) {
              annexures.push({
                title: s.title,
                fileName: s.attachmentFileName,
                url: s.attachmentUrl,
              });
            }
          }
          const out = await bundleReportWithAnnexures(
            { blob, fileName },
            annexures,
            `${title} (with annexures)`,
          );
          downloadBlob(out.blob, out.fileName);
          toast.success(
            annexures.length > 0
              ? `${format === "pdf" ? "PDF" : "Word document"} downloaded with ${annexures.length} annexure${annexures.length === 1 ? "" : "s"} (zip)`
              : format === "pdf"
                ? "PDF downloaded"
                : "Word document downloaded",
          );
          return;
        }

        setExportStage("saving");
        const uploadUrl = await generateUploadUrl({ jobId });
        const uploadResponse = await fetch(uploadUrl, {
          method: "POST",
          headers: { "Content-Type": mimeType },
          body: blob,
        });
        if (!uploadResponse.ok) throw new Error("Upload to storage failed");
        const { storageId }: { storageId: Id<"_storage"> } =
          await uploadResponse.json();
        await saveCanvasAsDocument({
          jobId,
          storageId,
          fileName,
          fileSize: blob.size,
          canvasTitle: title,
          fileType: mimeType,
        });
        toast.success("Exported to job documents");
      } catch (err) {
        toast.error(extractErrorMessage(err, "Export failed"));
      } finally {
        setExportStage(null);
      }
    },
    [
      report,
      jobId,
      currentUser,
      generateUploadUrl,
      saveCanvasAsDocument,
      sourceMode,
      citedSources,
      sections,
    ],
  );

  // MOR-866 — export the report as Word inside the firm template's own DOCX:
  // template styles/cover/headers/TOC survive; the report body is spliced in
  // verbatim server-side. First export of a template runs a one-time style
  // analysis (a minute or two); after that it's seconds.
  const handleStyledExport = useCallback(
    async (destination: ExportDestination) => {
      if (!report?.contentHtml) return;
      setExportStage("preparing");
      try {
        const title = report.title || "Report";
        // Same prep as the plain Word path: source-display mode + chart
        // rasterisation (the server can't run the chart renderer).
        const bodyForSourceMode =
          sourceMode !== "all"
            ? stripCitations(report.contentHtml)
            : report.contentHtml;
        const exportBody =
          sourceMode !== "none"
            ? bodyForSourceMode + buildBibliographyHtml(citedSources)
            : bodyForSourceMode;
        const html = DOMPurify.sanitize(exportBody, {
          ADD_ATTR: ["data-morgan-chart", "data-morgan-cite"],
        });
        const rendered = await renderCanvasBlocksForPrint(html, "word");

        if (report.templateStyledExport?.needsAnalysis) {
          toast.info(
            "Analysing the template's styling — the first styled export takes a minute or two.",
          );
        }

        const uploadUrl = await generateUploadUrl({ jobId });
        const htmlUpload = await fetch(uploadUrl, {
          method: "POST",
          headers: { "Content-Type": "text/html" },
          body: new Blob([rendered], { type: "text/html" }),
        });
        if (!htmlUpload.ok) throw new Error("Upload to storage failed");
        const {
          storageId: preparedHtmlStorageId,
        }: { storageId: Id<"_storage"> } = await htmlUpload.json();

        // The action reports its own stage (analysing/building) onto the
        // report row; the subscription streams it into the Export button.
        setExportStage("server");
        const result = await exportReportStyled({
          reportId,
          preparedHtmlStorageId,
        });
        for (const warning of result.warnings) toast.warning(warning);

        if (destination === "download") {
          setExportStage("downloading");
          const fileResponse = await fetch(result.url);
          if (!fileResponse.ok) throw new Error("Could not fetch the document");
          const blob = await fileResponse.blob();
          const { downloadBlob } =
            await import("@/utils/export/exportHtmlDocument");
          downloadBlob(blob, result.fileName);
          toast.success("Word document downloaded in the firm template");
        } else {
          setExportStage("saving");
          const fileResponse = await fetch(result.url);
          const fileSize = fileResponse.ok
            ? (await fileResponse.blob()).size
            : 0;
          await saveCanvasAsDocument({
            jobId,
            storageId: result.storageId,
            fileName: result.fileName,
            fileSize,
            canvasTitle: title,
            fileType: CANVAS_EXPORT_MIME.docx,
          });
          toast.success("Exported to job documents in the firm template");
        }
      } catch (err) {
        toast.error(extractErrorMessage(err, "Styled export failed"));
      } finally {
        setExportStage(null);
      }
    },
    [
      report,
      jobId,
      reportId,
      sourceMode,
      citedSources,
      generateUploadUrl,
      saveCanvasAsDocument,
      exportReportStyled,
    ],
  );

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (!report) {
    return (
      <div className="p-6 text-sm text-muted-foreground">Report not found.</div>
    );
  }

  const showReport =
    (status === "review" || status === "complete") && !!report.contentHtml;

  return (
    <ReportCitationContext.Provider value={citationCtx}>
      <div className="mx-auto flex h-[calc(100vh-6rem)] w-full max-w-[1600px] flex-col p-6">
        {/* Sticky under the app header (h-16) so the report actions stay
          reachable however far down the document you scroll. */}
        <div className="sticky top-16 z-20 -mx-2 mb-4 flex items-center justify-between gap-3 bg-background/95 px-2 py-2 backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="text-muted-foreground"
          >
            <ArrowLeft className="mr-1 size-4" />
            Reports
          </Button>
          <div className="flex items-center gap-2">
            {(status === "draft" ||
              status === "review" ||
              status === "complete") && (
              <Select
                value={status}
                onValueChange={(value) =>
                  void setReportStatus({
                    reportId,
                    status: value as "draft" | "review" | "complete",
                  }).catch((err) =>
                    toast.error(extractErrorMessage(err, "Failed")),
                  )
                }
              >
                <SelectTrigger
                  className="h-8 w-auto gap-1.5 text-sm font-medium"
                  aria-label="Report status"
                >
                  <CircleDot className="size-3.5 text-muted-foreground" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="review">Review</SelectItem>
                  <SelectItem value="complete">Complete</SelectItem>
                </SelectContent>
              </Select>
            )}
            {report.orgId && (
              <WorkflowStatusSelect
                orgId={report.orgId}
                value={report.workflowStatusId ?? null}
                onChange={(statusId) =>
                  void setReportWorkflowStatus({ reportId, statusId }).catch(
                    (err) =>
                      toast.error(
                        extractErrorMessage(
                          err,
                          "Failed to update workflow status",
                        ),
                      ),
                  )
                }
                placeholder="Firm status"
              />
            )}
            {showReport && (
              <Select
                value={sourceMode}
                onValueChange={(value) =>
                  setSourceMode(value as "all" | "bibliography" | "none")
                }
              >
                <SelectTrigger
                  className="h-8 w-auto gap-1.5 text-sm font-medium"
                  aria-label="Source display"
                >
                  <BookText className="size-3.5 text-muted-foreground" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sources</SelectItem>
                  <SelectItem value="bibliography">Sources at end</SelectItem>
                  <SelectItem value="none">No sources</SelectItem>
                </SelectContent>
              </Select>
            )}
            {showReport && !editing && !proposedHtml && (
              <Button
                size="sm"
                variant={pickMode ? "default" : "outline"}
                onClick={() => setPickMode((v) => !v)}
                title="Point at paragraphs, tables or rows to target them — click several to edit them together"
              >
                <SquareDashedMousePointer className="mr-1 size-3.5" />
                {pickMode
                  ? selections.length > 0
                    ? `Selecting… (${selections.length})`
                    : "Selecting…"
                  : selections.length > 0
                    ? `Selected (${selections.length})`
                    : "Select"}
              </Button>
            )}
            {showReport && (
              <Button
                size="sm"
                variant={chatOpen ? "default" : "outline"}
                onClick={() => setChatOpen((v) => !v)}
              >
                <MessageSquare className="mr-1 size-3.5" />
                Assistant
              </Button>
            )}
            {showReport && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setHistoryOpen(true)}
              >
                <History className="mr-1 size-3.5" />
                History
              </Button>
            )}
            {(status === "review" || status === "complete") &&
              report.contentHtml &&
              (editing ? (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditing(false)}
                  >
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleSaveEdits}>
                    <Check className="mr-1 size-4" />
                    Save
                  </Button>
                </>
              ) : (
                <>
                  <Button size="sm" variant="outline" onClick={startEditing}>
                    <Pencil className="mr-1 size-3.5" />
                    Edit
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="outline" disabled={exporting}>
                        {exporting ? (
                          <>
                            <Loader2 className="mr-1 size-4 animate-spin" />
                            {exportStageLabel ?? "Exporting…"}
                          </>
                        ) : (
                          <>
                            <Download className="mr-1 size-3.5" />
                            Export
                          </>
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-60">
                      <DropdownMenuItem
                        onClick={() => void handleExport("pdf", "download")}
                      >
                        <Download className="mr-2 size-3.5" />
                        Download as PDF
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => void handleExport("docx", "download")}
                      >
                        <Download className="mr-2 size-3.5" />
                        Download as Word (.docx)
                      </DropdownMenuItem>
                      {report.templateStyledExport?.available ? (
                        <DropdownMenuItem
                          onClick={() => void handleStyledExport("download")}
                        >
                          <Download className="mr-2 size-3.5" />
                          Download Word — firm template
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem
                          disabled
                          title={
                            report.templateStyledExport?.reason ??
                            "Firm-template export unavailable"
                          }
                        >
                          <Download className="mr-2 size-3.5" />
                          Download Word — firm template
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => void handleExport("pdf", "documents")}
                      >
                        <FileText className="mr-2 size-3.5" />
                        Save PDF to job documents
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => void handleExport("docx", "documents")}
                      >
                        <FileText className="mr-2 size-3.5" />
                        Save Word to job documents
                      </DropdownMenuItem>
                      {report.templateStyledExport?.available && (
                        <DropdownMenuItem
                          onClick={() => void handleStyledExport("documents")}
                        >
                          <FileText className="mr-2 size-3.5" />
                          Save Word (firm template) to documents
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try {
                        await startGeneration({ reportId });
                        toast.success("Regenerating…");
                      } catch (err) {
                        toast.error(extractErrorMessage(err, "Failed"));
                      }
                    }}
                  >
                    <RefreshCw className="mr-1 size-3.5" />
                    Regenerate
                  </Button>
                </>
              ))}
            <Button
              size="sm"
              variant="outline"
              className="text-muted-foreground hover:bg-destructive/10 hover:text-red-600"
              onClick={onRequestDelete}
              aria-label="Delete report"
            >
              <Trash2 className="size-3.5" />
            </Button>
          </div>
        </div>

        <div className="mb-1">
          <input
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
            }}
            aria-label="Report name"
            className="w-full rounded-md border border-transparent bg-transparent px-2 py-1 text-xl font-semibold outline-none hover:border-border focus:border-border focus:bg-background"
          />
        </div>
        {report.templateName && (
          <p className="mb-3 px-2 text-xs text-muted-foreground">
            From template: {report.templateName}
          </p>
        )}

        {/* Status-driven body — fills remaining height, panes scroll internally */}
        <div className="min-h-0 flex-1 overflow-hidden">
          {status === "draft" && (
            <div className="h-full overflow-auto pr-1">
              <DraftBody
                reportId={reportId}
                sections={sections}
                questionsStatus={report.questionsStatus}
                questions={report.clarifyingQuestions ?? []}
                onGenerate={async () => {
                  try {
                    await startGeneration({ reportId });
                    toast.success("Generating report…");
                  } catch (err) {
                    toast.error(extractErrorMessage(err, "Failed to start"));
                  }
                }}
              />
            </div>
          )}

          {status === "generating" && (
            <div className="h-full overflow-auto pr-1">
              <GeneratingBody
                sections={sections}
                done={doneCount}
                total={sections.length}
                assembling={sectionsDrafted}
              />
            </div>
          )}

          {status === "failed" && (
            <div className="h-full space-y-4 overflow-auto pr-1">
              <div className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                <span className="flex items-center gap-2">
                  <AlertCircle className="size-4 shrink-0" />
                  {report.error ??
                    "Generation failed. Please regenerate the report."}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    try {
                      await startGeneration({ reportId });
                      toast.success("Regenerating…");
                    } catch (err) {
                      toast.error(extractErrorMessage(err, "Failed"));
                    }
                  }}
                >
                  <RefreshCw className="mr-1 size-3.5" />
                  Regenerate
                </Button>
              </div>
              <SectionList sections={sections} />
            </div>
          )}

          {showReport && (
            <div className="flex h-full gap-4">
              <ReportTableOfContents
                headings={tocHeadings}
                bodyRef={reportBodyRef}
                open={tocOpen}
                onToggle={() => setTocOpen((v) => !v)}
              />
              <div
                ref={reportBodyRef}
                className="flex min-h-0 min-w-0 flex-1 flex-col"
              >
                <div className="min-h-0 flex-1">
                  {editing ? (
                    <div className="flex h-full flex-col gap-2">
                      {proposedHtml && (
                        <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                          <AlertCircle className="size-4 shrink-0" />
                          The assistant proposed a change while you were
                          editing. Save or cancel your edits to review it.
                        </div>
                      )}
                      <div className="min-h-0 flex-1">
                        <ReportEditor
                          initialHtml={editedHtml}
                          onChange={setEditedHtml}
                        />
                      </div>
                    </div>
                  ) : proposedHtml ? (
                    <div className="flex h-full flex-col overflow-hidden rounded-xl border bg-background">
                      <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                        <span className="flex items-center gap-2">
                          <Sparkles className="size-4 shrink-0" />
                          {report.proposalSummary ?? "Proposed changes"}
                        </span>
                        <div className="flex items-center gap-2">
                          <div className="flex overflow-hidden rounded-md border border-amber-300 text-xs dark:border-amber-800">
                            <button
                              type="button"
                              onClick={() => setDiffMode("diff")}
                              className={cn(
                                "px-2 py-1",
                                diffMode === "diff"
                                  ? "bg-amber-200/70 dark:bg-amber-900/60"
                                  : "hover:bg-amber-100/60 dark:hover:bg-amber-900/30",
                              )}
                            >
                              Changes
                            </button>
                            <button
                              type="button"
                              onClick={() => setDiffMode("after")}
                              className={cn(
                                "px-2 py-1",
                                diffMode === "after"
                                  ? "bg-amber-200/70 dark:bg-amber-900/60"
                                  : "hover:bg-amber-100/60 dark:hover:bg-amber-900/30",
                              )}
                            >
                              Preview
                            </button>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              try {
                                await rejectProposal({ reportId });
                              } catch (err) {
                                toast.error(extractErrorMessage(err, "Failed"));
                              }
                            }}
                          >
                            Reject
                          </Button>
                          <Button
                            size="sm"
                            onClick={async () => {
                              try {
                                await applyProposal({ reportId });
                                toast.success("Applied");
                              } catch (err) {
                                toast.error(extractErrorMessage(err, "Failed"));
                              }
                            }}
                          >
                            <Check className="mr-1 size-4" />
                            Apply
                          </Button>
                        </div>
                      </div>
                      {diffMode === "diff" ? (
                        <div ref={diffRef} className="h-full overflow-auto">
                          <div
                            className={cn(
                              "report-cite-markers prose prose-sm max-w-none px-10 py-10 dark:prose-invert",
                              sourceMode !== "all" && "hide-report-cites",
                            )}
                            // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- proposalDiff is DOMPurify.sanitize'd in its memo above (chart-aware diff of report HTML)
                            dangerouslySetInnerHTML={{ __html: proposalDiff }}
                          />
                          {/* Hovering a marker reveals its source chips, as in the
                          editor — the redline shows the marker alone. */}
                          <CitationLayer containerRef={diffRef} markersOnly />
                        </div>
                      ) : (
                        <ReportRenderer
                          html={proposedHtml}
                          className="h-full overflow-auto px-8 py-10"
                          footer={
                            sourceMode !== "none" ? (
                              <ReportSourcesList
                                sources={parseCitedSources(proposedHtml)}
                              />
                            ) : undefined
                          }
                        />
                      )}
                    </div>
                  ) : (
                    <ReportRenderer
                      html={report.contentHtml ?? ""}
                      className="h-full overflow-auto rounded-xl border bg-background px-8 py-10"
                      editingSections={editingSections}
                      taggedSection={taggedSection}
                      footer={
                        sourceMode !== "none" ? (
                          <ReportSourcesList sources={citedSources} />
                        ) : undefined
                      }
                    />
                  )}
                </div>
                <ReportAttachmentsPanel
                  jobId={jobId}
                  attachments={sections.filter(
                    (s) => s.fillMode === "attachment",
                  )}
                />
              </div>
              {chatOpen && (
                <ReportChatPanel
                  reportId={reportId}
                  jobId={jobId}
                  sectionHeadings={sectionHeadings}
                  onTagSection={setTaggedSection}
                  selections={selections}
                  onRemoveSelection={removeSelection}
                  onClearSelections={clearSelections}
                  proposedHtml={proposedHtml}
                  proposalSummary={report.proposalSummary ?? null}
                  draft={reconcileDraft}
                  onDraftConsumed={() => setReconcileDraft(null)}
                  impactScan={impactScan ?? null}
                  onJumpToImpact={handleJumpToImpact}
                  onReconcileImpact={handleReconcileImpact}
                  onReconcileAllImpact={handleReconcileAllImpact}
                  onDismissImpact={handleDismissImpact}
                  wide={chatWide}
                  onToggleWide={() => setChatWide((v) => !v)}
                  onClose={() => setChatOpen(false)}
                />
              )}
            </div>
          )}
        </div>

        <ReportElementPicker
          active={pickMode}
          bodyRef={reportBodyRef}
          onPick={handlePick}
          onCancel={() => setPickMode(false)}
        />

        <DocumentDetail
          documentId={previewDocId}
          isViewer={false}
          open={!!previewDocId}
          onClose={() => setPreviewDocId(null)}
        />

        <PodClaimOverlay
          jobId={jobId}
          claimId={recordClaimId}
          onClose={() => setRecordClaimId(null)}
        />

        <ReportHistoryPanel
          reportId={reportId}
          currentHtml={report.contentHtml ?? ""}
          open={historyOpen}
          onOpenChange={setHistoryOpen}
        />
      </div>
    </ReportCitationContext.Provider>
  );
}

/**
 * Table of contents down the left of a rendered report. Clicking an entry
 * scrolls that heading into view; the entry for the section currently at the
 * top of the reading pane stays highlighted. Collapses to a narrow rail so
 * the report itself can have the width back.
 */
function ReportTableOfContents({
  headings,
  bodyRef,
  open,
  onToggle,
}: {
  headings: Array<{ text: string; level: number }>;
  /** The report pane — searched for the heading elements to scroll to. */
  bodyRef: React.RefObject<HTMLDivElement | null>;
  open: boolean;
  onToggle: () => void;
}) {
  const [activeText, setActiveText] = useState<string | null>(null);

  // Highlight whichever heading is currently at the top of the reading pane.
  // The scroll container is a descendant that differs between the read, edit
  // and diff panes, so listen in the capture phase (scroll doesn't bubble)
  // rather than trying to identify it.
  useEffect(() => {
    if (!open || headings.length === 0) return;
    const body = bodyRef.current;
    if (!body) return;

    let frame = 0;
    const sync = () => {
      frame = 0;
      const top = body.getBoundingClientRect().top;
      const current = Array.from(
        body.querySelectorAll<HTMLElement>("h1, h2, h3"),
      ).reduce<string | null>(
        (acc, h) =>
          h.getBoundingClientRect().top - top <= 24
            ? (h.textContent?.trim() ?? null)
            : acc,
        null,
      );
      setActiveText(current ?? headings[0]?.text ?? null);
    };
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(sync);
    };
    // Content mounts asynchronously (TipTap), so take a second reading once
    // it has painted.
    const initial = window.setTimeout(sync, 120);
    sync();
    body.addEventListener("scroll", onScroll, {
      capture: true,
      passive: true,
    });
    return () => {
      window.clearTimeout(initial);
      if (frame) cancelAnimationFrame(frame);
      body.removeEventListener("scroll", onScroll, { capture: true });
    };
  }, [open, headings, bodyRef]);

  const scrollTo = (text: string) => {
    const body = bodyRef.current;
    if (!body) return;
    const target = Array.from(
      body.querySelectorAll<HTMLElement>("h1, h2, h3"),
    ).find((h) => h.textContent?.trim() === text);
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveText(text);
  };

  if (headings.length === 0) return null;

  if (!open) {
    return (
      <div className="flex h-full shrink-0 flex-col items-center rounded-xl border bg-background px-1 py-2">
        <button
          type="button"
          onClick={onToggle}
          aria-label="Show contents"
          title="Show contents"
          className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <ListTree className="size-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-full w-[240px] shrink-0 flex-col overflow-hidden rounded-xl border bg-background">
      <div className="flex items-center justify-between border-b px-3 py-2">
        <span className="flex items-center gap-1.5 text-sm font-medium">
          <ListTree className="size-4 text-muted-foreground" />
          Contents
        </span>
        <button
          type="button"
          onClick={onToggle}
          aria-label="Hide contents"
          title="Hide contents"
          className="text-muted-foreground hover:text-foreground"
        >
          <PanelLeftClose className="size-4" />
        </button>
      </div>
      <nav className="min-h-0 flex-1 overflow-auto py-2">
        {headings.map((h) => {
          const active = activeText === h.text;
          return (
            <button
              key={h.text}
              type="button"
              onClick={() => scrollTo(h.text)}
              className={cn(
                "block w-full border-l-2 py-1.5 pr-3 text-left text-xs leading-snug transition-colors",
                h.level >= 3 ? "pl-7" : "pl-4",
                active
                  ? "border-primary font-medium text-foreground"
                  : "border-transparent text-muted-foreground hover:bg-muted/40 hover:text-foreground",
                hasUnfilledPlaceholder(h.text) &&
                  !active &&
                  "text-amber-800 dark:text-amber-200",
              )}
            >
              {hasUnfilledPlaceholder(h.text) && (
                <AlertCircle
                  className="mb-px mr-1 inline size-3 text-amber-600 dark:text-amber-400"
                  aria-label="Contains unfilled fields"
                />
              )}
              {h.text}
            </button>
          );
        })}
      </nav>
    </div>
  );
}

function ReportChatPanel({
  reportId,
  jobId,
  sectionHeadings,
  onTagSection,
  selections,
  onRemoveSelection,
  onClearSelections,
  draft,
  onDraftConsumed,
  impactScan,
  onJumpToImpact,
  onReconcileImpact,
  onReconcileAllImpact,
  onDismissImpact,
  wide,
  onToggleWide,
  onClose,
}: {
  reportId: Id<"jobReports">;
  jobId: Id<"jobs">;
  /** Headings in the report body — offered as @-mention targets (MOR-917). */
  sectionHeadings: string[];
  /** Fired when the user tags a section, so the report view highlights it. */
  onTagSection: (title: string | null) => void;
  /** Elements picked in the report, targeted by the next message. */
  selections: ReportSelection[];
  onRemoveSelection: (index: number) => void;
  onClearSelections: () => void;
  proposedHtml: string | null;
  proposalSummary: string | null;
  /** Pre-fills the composer (e.g. a figure-impact "reconcile" instruction). */
  draft?: string | null;
  /** Called once the draft has been applied to the composer. */
  onDraftConsumed?: () => void;
  /** Latest figure-impact scan, surfaced as a card at the top of the chat. */
  impactScan: FigureImpactScan | null;
  onJumpToImpact: (result: FigureImpactResult) => void;
  onReconcileImpact: (result: FigureImpactResult) => void;
  onReconcileAllImpact: () => void;
  onDismissImpact: () => void;
  wide: boolean;
  onToggleWide: () => void;
  onClose: () => void;
}) {
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [attachedDocs, setAttachedDocs] = useState<ChatDocPick[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Composer seed from a figure-impact "reconcile" action. Bumping the key
  // remounts the composer so the text seeds even when the same draft repeats.
  const [composerDraft, setComposerDraft] = useState("");
  const [composerSeq, setComposerSeq] = useState(0);
  useEffect(() => {
    if (!draft) return;
    setComposerDraft(draft);
    setComposerSeq((n) => n + 1);
    onDraftConsumed?.();
  }, [draft, onDraftConsumed]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { data: messages } = useQuery(
    convexQuery(api.jobs.reporting.queries.listReportMessages, { reportId }),
  );
  const { data: jobDocs } = useQuery(
    convexQuery(api.jobs.gatherInformation.documents.queries.listDocuments, {
      jobId,
    }),
  );
  const { mutateAsync: sendChatMessage, isPending } = useMutation({
    mutationFn: useConvexMutation(api.jobs.reporting.mutations.sendChatMessage),
  });

  // @-mention items: the report's sections first, then the job's documents.
  const SECTION_MENTION_PREFIX = "section:";
  const mentionItems: ChatMentionItem[] = useMemo(
    () => [
      ...sectionHeadings.map((h) => ({
        id: `${SECTION_MENTION_PREFIX}${h}`,
        name: h,
        subtitle: "Section",
      })),
      ...(jobDocs ?? []).map((d) => ({
        id: d._id,
        name: d.fileName,
        subtitle: "Document",
      })),
    ],
    [sectionHeadings, jobDocs],
  );
  const addDocs = useCallback((picks: ChatDocPick[]) => {
    setAttachedDocs((prev) => {
      const have = new Set(prev.map((d) => d.documentId));
      return [...prev, ...picks.filter((p) => !have.has(p.documentId))].slice(
        0,
        10,
      );
    });
  }, []);
  const handleMentionSelect = useCallback(
    (item: ChatMentionItem) => {
      if (item.id.startsWith(SECTION_MENTION_PREFIX)) {
        onTagSection(item.id.slice(SECTION_MENTION_PREFIX.length));
        return;
      }
      const doc = (jobDocs ?? []).find((d) => d._id === item.id);
      if (doc) {
        addDocs([
          {
            documentId: doc._id,
            fileName: doc.fileName,
            jobId,
          },
        ]);
      }
    },
    [onTagSection, jobDocs, addDocs, jobId],
  );

  type ReportMsg = ChatMessageData & { kind?: "message" | "tool" };
  const realMessages: ReportMsg[] = useMemo(
    () =>
      (messages ?? []).map((m) => ({
        id: m._id,
        role: m.role,
        content: m.content,
        createdAt: m.createdAt,
        kind: m.kind,
      })),
    [messages],
  );

  const chatMessages: ReportMsg[] = useMemo(() => {
    if (!pendingMessage) return realMessages;
    return [
      ...realMessages,
      {
        id: "optimistic-pending",
        role: "user" as const,
        content: pendingMessage,
        createdAt: Date.now(),
      },
    ];
  }, [realMessages, pendingMessage]);

  useEffect(() => {
    if (!pendingMessage) return;
    if (
      realMessages.some(
        (m) => m.role === "user" && m.content.endsWith(pendingMessage),
      )
    ) {
      setPendingMessage(null);
    }
  }, [realMessages, pendingMessage]);

  // Auto-scroll to the latest message.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chatMessages.length, isPending]);

  const lastMessage = chatMessages[chatMessages.length - 1];
  const isThinking =
    isPending || lastMessage?.role === "user" || lastMessage?.kind === "tool";

  const handleSend = useCallback(
    async (raw: string) => {
      const text = raw.trim();
      if (!text) return;
      // Name picker-attached files in the message itself (MOR-890), so the
      // bubble renders them as chips and the transcript records what was sent.
      const unmentioned = attachedDocs.filter(
        (d) => !text.includes(`@${d.fileName}`),
      );
      const content =
        unmentioned.length > 0
          ? `${text}\n\nAttached: ${unmentioned.map((d) => d.fileName).join(", ")}`
          : text;
      setPendingMessage(content);
      // Sections whose @Title actually appears in the sent text (the user may
      // have deleted a tag after selecting it).
      const taggedSections = sectionHeadings.filter((h) =>
        text.includes(`@${h}`),
      );
      const attachedDocumentIds = attachedDocs.map((d) => d.documentId);
      try {
        await sendChatMessage({
          reportId,
          message: content,
          taggedSections:
            taggedSections.length > 0 ? taggedSections : undefined,
          attachedDocumentIds:
            attachedDocumentIds.length > 0 ? attachedDocumentIds : undefined,
          selections:
            selections.length > 0 ? selections.map(toWireSelection) : undefined,
        });
        setAttachedDocs([]);
        onClearSelections();
      } catch (err) {
        toast.error(extractErrorMessage(err, "Failed to send"));
        setPendingMessage(null);
      }
    },
    [
      reportId,
      sendChatMessage,
      sectionHeadings,
      attachedDocs,
      selections,
      onClearSelections,
    ],
  );

  return (
    <div
      className={cn(
        "flex h-full shrink-0 flex-col overflow-hidden rounded-xl border bg-background transition-[width]",
        wide ? "w-[520px]" : "w-[380px]",
      )}
    >
      <div className="flex items-center justify-between border-b px-3 py-2">
        <span className="flex items-center gap-1.5 text-sm font-medium">
          <Logo compact className="size-4 text-primary" />
          Report assistant
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onToggleWide}
            className="text-muted-foreground hover:text-foreground"
            aria-label={wide ? "Contract" : "Expand"}
            title={wide ? "Contract" : "Expand"}
          >
            {wide ? (
              <PanelRightOpen className="size-4" />
            ) : (
              <PanelRightClose className="size-4" />
            )}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Close assistant"
          >
            <X className="size-4" />
          </button>
        </div>
      </div>

      <div ref={scrollRef} className="min-h-0 flex-1 overflow-auto py-2">
        {impactScan && (
          <FigureImpactPanel
            scan={impactScan}
            onJumpTo={onJumpToImpact}
            onReconcile={onReconcileImpact}
            onReconcileAll={onReconcileAllImpact}
            onDismiss={onDismissImpact}
          />
        )}
        {chatMessages.length === 0 && (
          <p className="px-5 py-6 text-sm text-muted-foreground">
            Ask assistant to revise the report — copy changes, graphics or tone.
          </p>
        )}
        {chatMessages.map((m, i) =>
          m.kind === "tool" ? (
            <ToolPill
              key={m.id}
              label={m.content}
              active={i === chatMessages.length - 1 && isThinking}
            />
          ) : (
            <ChatMessage key={m.id} message={m} />
          ),
        )}
        {isThinking && lastMessage?.kind !== "tool" && (
          <div className="flex items-center gap-2 px-5 py-2 text-sm text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" />
            Thinking…
          </div>
        )}
      </div>

      <div className="border-t p-3">
        {selections.length > 0 && (
          <div className="mx-4 mb-1.5 flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Editing {selections.length} selected
                {selections.length === 1 ? " item" : " items"}
              </p>
              <button
                type="button"
                onClick={onClearSelections}
                className="text-[11px] text-muted-foreground hover:text-foreground"
              >
                Clear all
              </button>
            </div>
            <div className="flex max-h-32 flex-col gap-1 overflow-auto">
              {selections.map((sel, i) => (
                <div
                  key={`${sel.sectionTitle ?? ""}:${sel.blockIndex}:${i}`}
                  className="flex items-start gap-2 rounded-lg border border-primary/40 bg-primary/5 px-2.5 py-1.5"
                >
                  <SquareDashedMousePointer className="mt-0.5 size-3.5 shrink-0 text-primary" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium">
                      {selectionChipLabel(sel)}
                    </p>
                    {sel.excerpt && (
                      <p className="truncate text-[11px] text-muted-foreground">
                        {sel.excerpt}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => onRemoveSelection(i)}
                    aria-label={`Remove ${selectionChipLabel(sel)}`}
                    className="shrink-0 text-muted-foreground hover:text-foreground"
                  >
                    <X className="size-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        <ChatInput
          key={composerSeq}
          initialValue={composerDraft}
          onSend={(m) => void handleSend(m)}
          isDisabled={isPending}
          placeholder={
            selections.length === 1
              ? `Change this ${selections[0].label.toLowerCase()}…`
              : selections.length > 1
                ? `Change these ${selections.length} items…`
                : "Ask for a change…"
          }
          mentionItems={mentionItems}
          mentionLabel="Tag a section or document"
          onMentionSelect={handleMentionSelect}
          attachments={attachedDocs.map((d) => ({
            id: d.documentId,
            name: d.fileName,
          }))}
          onRemoveAttachment={(id) =>
            setAttachedDocs((prev) => prev.filter((d) => d.documentId !== id))
          }
          onAttachClick={() => setPickerOpen(true)}
        />
      </div>

      <JobChatDocumentPicker
        jobId={jobId}
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(picks) => {
          addDocs(picks);
          setPickerOpen(false);
        }}
      />
    </div>
  );
}

type VersionSource = "generation" | "manual_edit" | "ai_apply" | "restore";

interface VersionRow {
  _id: Id<"jobReportVersions">;
  createdAt: number;
  source: VersionSource;
  summary: string;
  instruction: string | null;
  createdByName: string | null;
  instructedByName: string | null;
  restoredFromVersionId: Id<"jobReportVersions"> | null;
}

const SOURCE_META: Record<
  VersionSource,
  { label: string; color: "gray" | "blue" | "violet" | "amber" }
> = {
  generation: { label: "Generated", color: "blue" },
  manual_edit: { label: "Manual edit", color: "gray" },
  ai_apply: { label: "Assistant", color: "violet" },
  restore: { label: "Restored", color: "amber" },
};

function formatVersionTime(ts: number): string {
  return new Date(ts).toLocaleString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Sanitised HTML diff that mounts any chart blocks kept intact by the
 * chart-aware diff, so added/removed charts render inside the redline.
 */
function DiffView({ html }: { html: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const rootsRef = useRef<Root[]>([]);
  const safe = useMemo(
    () =>
      DOMPurify.sanitize(html, {
        ADD_ATTR: ["data-morgan-chart", "data-morgan-cite", "data-cite-text"],
      }),
    [html],
  );
  useEffect(() => {
    const unmount = () => {
      rootsRef.current.forEach((r) => r.unmount());
      rootsRef.current = [];
    };
    const t = setTimeout(() => {
      const container = ref.current;
      if (!container) return;
      container
        .querySelectorAll<HTMLElement>("div[data-morgan-chart]")
        .forEach((el) => {
          const spec = chartSpecFromAttr(
            el.getAttribute("data-morgan-chart") ?? "",
          );
          if (!spec) return;
          el.innerHTML = "";
          const root = createRoot(el);
          root.render(
            createElement(
              "div",
              { className: "rounded-lg border bg-background p-3" },
              createElement(
                "p",
                { className: "mb-2 text-sm font-semibold" },
                spec.title,
              ),
              createElement(ChartRenderer, { spec }),
            ),
          );
          rootsRef.current.push(root);
        });
    }, 60);
    return () => {
      clearTimeout(t);
      unmount();
    };
  }, [safe]);
  return (
    <div
      ref={ref}
      className="prose prose-sm max-w-none dark:prose-invert"
      // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- safe is DOMPurify.sanitize'd in the memo above
      dangerouslySetInnerHTML={{ __html: safe }}
    />
  );
}

/** Right-side drawer: the report's change log, with preview + restore. */
function ReportHistoryPanel({
  reportId,
  currentHtml,
  open,
  onOpenChange,
}: {
  reportId: Id<"jobReports">;
  currentHtml: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [selectedId, setSelectedId] = useState<Id<"jobReportVersions"> | null>(
    null,
  );
  const [restoreTarget, setRestoreTarget] = useState<VersionRow | null>(null);
  const [restoring, setRestoring] = useState(false);

  const { data: versions, isLoading } = useQuery({
    ...convexQuery(api.jobs.reporting.queries.listReportVersions, { reportId }),
    enabled: open,
  });
  const { mutateAsync: restoreVersion } = useMutation({
    mutationFn: useConvexMutation(api.jobs.reporting.mutations.restoreVersion),
  });

  // Reset the open preview whenever the drawer closes.
  useEffect(() => {
    if (!open) setSelectedId(null);
  }, [open]);

  const rows = versions ?? [];
  const selected = rows.find((r) => r._id === selectedId) ?? null;
  // The newest entry (index 0) is the live content — restoring it is a no-op.
  const isCurrent = (v: VersionRow) => rows[0]?._id === v._id;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[680px]"
      >
        <SheetHeader className="space-y-1 border-b px-5 py-4 text-left">
          <SheetTitle className="flex items-center gap-2 text-base">
            <History className="size-4" />
            Change history
          </SheetTitle>
          <SheetDescription>
            Every change to this report. Restoring an earlier version adds it as
            the newest change — nothing in the log is lost.
          </SheetDescription>
        </SheetHeader>

        {selected ? (
          <VersionDetail
            version={selected}
            currentHtml={currentHtml}
            isCurrent={isCurrent(selected)}
            onBack={() => setSelectedId(null)}
            onRestore={() => setRestoreTarget(selected)}
          />
        ) : (
          <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
            {isLoading ? (
              <div className="flex justify-center py-10">
                <Spinner />
              </div>
            ) : rows.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground">
                No changes recorded yet.
              </p>
            ) : (
              <ol className="relative flex flex-col gap-1.5 border-l border-border/70 pl-4">
                {rows.map((v) => {
                  const meta = SOURCE_META[v.source];
                  return (
                    <li key={v._id} className="relative">
                      <span className="absolute -left-[1.30rem] top-3 size-2 rounded-full bg-border ring-4 ring-background" />
                      <button
                        type="button"
                        onClick={() => setSelectedId(v._id)}
                        className="group w-full rounded-lg border border-transparent px-3 py-2.5 text-left hover:border-border hover:bg-muted/40"
                      >
                        <div className="flex items-center gap-2">
                          <Badge color={meta.color} size="sm">
                            {meta.label}
                          </Badge>
                          {isCurrent(v) && (
                            <Badge color="green" size="sm">
                              Current
                            </Badge>
                          )}
                          <span className="ml-auto text-xs text-muted-foreground">
                            {formatVersionTime(v.createdAt)}
                          </span>
                        </div>
                        <p className="mt-1.5 text-sm font-medium text-foreground">
                          {v.summary}
                        </p>
                        <VersionByline version={v} />
                      </button>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        )}

        <AlertDialog
          open={!!restoreTarget}
          onOpenChange={(o) => !o && setRestoreTarget(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Restore this version?</AlertDialogTitle>
              <AlertDialogDescription>
                The report will be set back to this version
                {restoreTarget
                  ? ` (${formatVersionTime(restoreTarget.createdAt)})`
                  : ""}
                . This is added as the newest change, so the current content is
                preserved in the log and you can roll forward again.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={async () => {
                  if (!restoreTarget) return;
                  setRestoring(true);
                  try {
                    await restoreVersion({ versionId: restoreTarget._id });
                    toast.success("Version restored");
                    setRestoreTarget(null);
                    setSelectedId(null);
                    onOpenChange(false);
                  } catch (err) {
                    toast.error(extractErrorMessage(err, "Failed to restore"));
                  } finally {
                    setRestoring(false);
                  }
                }}
              >
                {restoring && <Loader2 className="mr-1 size-4 animate-spin" />}
                Restore
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
}

/** "by <author>" + "instructed by <human>" line for a version row. */
function VersionByline({ version }: { version: VersionRow }) {
  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
      {version.createdByName && (
        <span className="inline-flex items-center gap-1">
          <User className="size-3" />
          {version.source === "ai_apply" ? "applied by " : "by "}
          {version.createdByName}
        </span>
      )}
      {version.source === "ai_apply" && version.instructedByName && (
        <span className="inline-flex items-center gap-1">
          <Sparkles className="size-3 text-primary" />
          instructed by {version.instructedByName}
        </span>
      )}
    </div>
  );
}

/** Preview a single version: its content, or the changes since it, + restore. */
function VersionDetail({
  version,
  currentHtml,
  isCurrent,
  onBack,
  onRestore,
}: {
  version: VersionRow;
  currentHtml: string;
  isCurrent: boolean;
  onBack: () => void;
  onRestore: () => void;
}) {
  const [view, setView] = useState<"content" | "changes">("content");
  const { data: full, isLoading } = useQuery(
    convexQuery(api.jobs.reporting.queries.getReportVersion, {
      versionId: version._id,
    }),
  );
  const versionHtml = full?.contentHtml;
  const changesDiff = useMemo(
    () =>
      versionHtml !== undefined
        ? buildChartAwareDiff(versionHtml, currentHtml)
        : "",
    [versionHtml, currentHtml],
  );
  const meta = SOURCE_META[version.source];

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-2 border-b px-5 py-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="text-muted-foreground"
        >
          <ArrowLeft className="mr-1 size-4" />
          All changes
        </Button>
        <div className="ml-auto flex items-center gap-2">
          {!isCurrent && (
            <div className="flex overflow-hidden rounded-md border text-xs">
              <button
                type="button"
                onClick={() => setView("content")}
                className={cn(
                  "px-2 py-1",
                  view === "content" ? "bg-muted" : "hover:bg-muted/60",
                )}
              >
                This version
              </button>
              <button
                type="button"
                onClick={() => setView("changes")}
                className={cn(
                  "px-2 py-1",
                  view === "changes" ? "bg-muted" : "hover:bg-muted/60",
                )}
              >
                Changes since
              </button>
            </div>
          )}
          <Button size="sm" onClick={onRestore} disabled={isCurrent}>
            <RotateCcw className="mr-1 size-3.5" />
            Restore
          </Button>
        </div>
      </div>

      <div className="border-b bg-muted/20 px-5 py-2.5">
        <div className="flex items-center gap-2">
          <Badge color={meta.color} size="sm">
            {meta.label}
          </Badge>
          {isCurrent && (
            <Badge color="green" size="sm">
              Current
            </Badge>
          )}
          <span className="text-xs text-muted-foreground">
            {formatVersionTime(version.createdAt)}
          </span>
        </div>
        <p className="mt-1 text-sm font-medium">{version.summary}</p>
        <VersionByline version={version} />
        {version.source === "ai_apply" && version.instruction && (
          <p className="mt-1.5 rounded-md bg-muted/60 px-2.5 py-1.5 text-xs italic text-muted-foreground">
            “{version.instruction}”
          </p>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-auto px-5 py-5">
        {isLoading || versionHtml === undefined ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : isCurrent || view === "content" ? (
          <ReportRenderer html={versionHtml} />
        ) : changesDiff.trim() ? (
          <DiffView html={changesDiff} />
        ) : (
          <p className="py-10 text-center text-sm text-muted-foreground">
            No differences from the current report.
          </p>
        )}
      </div>
    </div>
  );
}

/** A tool-call pill for a sub-agent dispatch step in the report chat. */
function ToolPill({ label, active }: { label: string; active: boolean }) {
  return (
    <div className="px-5 py-1.5">
      <div className="inline-flex items-center gap-2 rounded-lg border bg-muted/40 px-2.5 py-1.5 text-xs text-muted-foreground">
        {active ? (
          <Loader2 className="size-3.5 animate-spin text-primary" />
        ) : (
          <Wrench className="size-3.5 text-primary" />
        )}
        <span className="font-medium text-foreground">{label}</span>
      </div>
    </div>
  );
}

type QuestionsStatus = "generating" | "ready" | "skipped" | "failed";
interface QuestionAnswer {
  question: string;
  answer: string;
}

/**
 * The pre-generation questions, stepped through one at a time in a modal
 * (MOR-918). Morgan pre-fills each answer from the job's data; the user
 * confirms or edits, moves on, and generates at the end — or skips the whole
 * thing. Their answers become authoritative direction for every section
 * writer.
 */
function ReportQuestionsDialog({
  open,
  onOpenChange,
  questions,
  busy,
  onChange,
  onSkip,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  questions: QuestionAnswer[];
  busy: boolean;
  onChange: (next: QuestionAnswer[]) => void;
  onSkip: () => void;
  onSubmit: () => void;
}) {
  const [step, setStep] = useState(0);
  // Re-opening (or a fresh set of questions) starts from the top.
  useEffect(() => {
    if (open) setStep(0);
  }, [open, questions.length]);

  const total = questions.length;
  const current = questions[step];
  const isLast = step >= total - 1;
  if (total === 0) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="size-4 text-primary" />A few questions before
            drafting
          </DialogTitle>
          <DialogDescription>
            Morgan pre-filled its best answer from this job's data. Confirm or
            edit each one — your answers steer every section of the report.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          {questions.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Question ${i + 1}`}
              onClick={() => setStep(i)}
              className={cn(
                "h-1.5 flex-1 rounded-full transition-colors",
                i === step
                  ? "bg-primary"
                  : i < step
                    ? "bg-primary/40"
                    : "bg-muted",
              )}
            />
          ))}
        </div>

        {current && (
          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Question {step + 1} of {total}
            </p>
            <p className="mb-3 text-sm font-medium">{current.question}</p>
            <textarea
              // Remount per step so the textarea takes the new answer and
              // autofocus lands on each question in turn.
              key={step}
              autoFocus
              value={current.answer}
              onChange={(e) =>
                onChange(
                  questions.map((q, j) =>
                    j === step ? { ...q, answer: e.target.value } : q,
                  ),
                )
              }
              rows={5}
              placeholder="Your answer…"
              className="w-full resize-y rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus-visible:border-primary/40"
            />
          </div>
        )}

        <DialogFooter className="sm:justify-between">
          <Button variant="ghost" disabled={busy} onClick={onSkip}>
            Skip all
          </Button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => setStep((s) => s - 1)}
              >
                Back
              </Button>
            )}
            {isLast ? (
              <Button disabled={busy} onClick={onSubmit}>
                {busy ? (
                  <Loader2 className="mr-1 size-4 animate-spin" />
                ) : (
                  <Sparkles className="mr-1 size-4" />
                )}
                Generate report
              </Button>
            ) : (
              <Button disabled={busy} onClick={() => setStep((s) => s + 1)}>
                Continue
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Draft state: the seeded outline plus the pre-generation questions step
 * (MOR-918). Generating first has Morgan raise a few questions with
 * pre-filled answers the practitioner can adjust; the step is skippable.
 */
function DraftBody({
  reportId,
  sections,
  questionsStatus,
  questions,
  onGenerate,
}: {
  reportId: Id<"jobReports">;
  sections: SectionRow[];
  questionsStatus: QuestionsStatus | undefined;
  questions: QuestionAnswer[];
  onGenerate: () => void | Promise<void>;
}) {
  const { mutateAsync: prepareQuestions } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.reporting.mutations.prepareQuestions,
    ),
  });
  const { mutateAsync: saveQuestionAnswers } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.reporting.mutations.saveQuestionAnswers,
    ),
  });
  const { mutateAsync: skipQuestions } = useMutation({
    mutationFn: useConvexMutation(api.jobs.reporting.mutations.skipQuestions),
  });
  const [answers, setAnswers] = useState<QuestionAnswer[]>(questions);
  useEffect(() => {
    setAnswers(questions);
  }, [questions]);
  const [busy, setBusy] = useState(false);
  const [questionsOpen, setQuestionsOpen] = useState(false);
  // Pop the questions out as soon as they land — the user asked for them, so
  // they shouldn't have to go looking.
  useEffect(() => {
    if (questionsStatus === "ready") setQuestionsOpen(true);
  }, [questionsStatus]);

  const skipAndGenerate = async () => {
    setBusy(true);
    try {
      await skipQuestions({ reportId });
      await onGenerate();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to start"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {questionsStatus === undefined && (
        <div className="flex items-center justify-between gap-3 rounded-lg border bg-muted/30 px-4 py-3">
          <p className="text-sm text-muted-foreground">
            {sections.length} sections seeded from the template. Morgan will
            first ask a few quick questions to shape the report — or generate
            straight away.
          </p>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={skipAndGenerate}
            >
              Generate without questions
            </Button>
            <Button
              size="sm"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await prepareQuestions({ reportId });
                } catch (err) {
                  toast.error(extractErrorMessage(err, "Failed to start"));
                } finally {
                  setBusy(false);
                }
              }}
            >
              <Sparkles className="mr-1 size-4" />
              Generate report
            </Button>
          </div>
        </div>
      )}

      {questionsStatus === "generating" && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          <span className="flex items-center gap-2">
            <Loader2 className="size-4 animate-spin" />
            Morgan is preparing a few questions about this report…
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={skipAndGenerate}
          >
            Skip and generate
          </Button>
        </div>
      )}

      {questionsStatus === "ready" && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-primary/40 bg-primary/5 px-4 py-3">
          <p className="flex items-center gap-2 text-sm">
            <Sparkles className="size-4 shrink-0 text-primary" />
            Morgan has {answers.length} question
            {answers.length === 1 ? "" : "s"} before drafting.
          </p>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={skipAndGenerate}
            >
              Skip
            </Button>
            <Button size="sm" onClick={() => setQuestionsOpen(true)}>
              Review questions
            </Button>
          </div>
        </div>
      )}

      <ReportQuestionsDialog
        open={questionsOpen && questionsStatus === "ready"}
        onOpenChange={setQuestionsOpen}
        questions={answers}
        busy={busy}
        onChange={setAnswers}
        onSkip={skipAndGenerate}
        onSubmit={async () => {
          setBusy(true);
          try {
            await saveQuestionAnswers({ reportId, questions: answers });
            setQuestionsOpen(false);
            await onGenerate();
          } catch (err) {
            toast.error(extractErrorMessage(err, "Failed to start"));
          } finally {
            setBusy(false);
          }
        }}
      />

      {(questionsStatus === "failed" || questionsStatus === "skipped") && (
        <div className="flex items-center justify-between gap-3 rounded-lg border bg-muted/30 px-4 py-3">
          <p className="text-sm text-muted-foreground">
            {questionsStatus === "failed"
              ? "Morgan couldn't prepare questions this time — you can generate straight away."
              : `${sections.length} sections seeded from the template. Generate to draft them from this job's data.`}
          </p>
          <div className="flex shrink-0 items-center gap-2">
            {questionsStatus === "failed" && (
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await prepareQuestions({ reportId });
                  } catch (err) {
                    toast.error(extractErrorMessage(err, "Failed"));
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Retry questions
              </Button>
            )}
            <Button size="sm" disabled={busy} onClick={() => void onGenerate()}>
              <Sparkles className="mr-1 size-4" />
              Generate report
            </Button>
          </div>
        </div>
      )}

      <SectionList sections={sections} />
    </div>
  );
}

function GeneratingBody({
  sections,
  done,
  total,
  assembling,
}: {
  sections: SectionRow[];
  done: number;
  total: number;
  assembling: boolean;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
        <Loader2 className="size-4 animate-spin" />
        {assembling
          ? "Assembling the report…"
          : `Drafting sections… ${done}/${total} done`}
      </div>
      <SectionList sections={sections} />
    </div>
  );
}

function SectionList({ sections }: { sections: SectionRow[] }) {
  return (
    <div className="flex flex-col gap-1">
      {sections.map((s) => (
        <div
          key={s._id}
          className={cn(
            "flex items-center gap-2 rounded-md border px-3 py-2 text-sm",
            s.parentSectionId && "ml-6",
          )}
        >
          <SectionStatusIcon status={s.status} fillMode={s.fillMode} />
          <span className="flex-1 truncate">{s.title}</span>
          {s.fillMode === "attachment" && (
            <Badge color="amber" size="sm">
              <Paperclip className="mr-1 size-3" />
              Annexure
            </Badge>
          )}
        </div>
      ))}
    </div>
  );
}

function SectionStatusIcon({
  status,
  fillMode,
}: {
  status: SectionRow["status"];
  fillMode: SectionRow["fillMode"];
}) {
  if (fillMode === "attachment") {
    return <Paperclip className="size-3.5 shrink-0 text-amber-500" />;
  }
  if (status === "writing") {
    return <Loader2 className="size-3.5 shrink-0 animate-spin text-blue-500" />;
  }
  if (status === "written" || status === "reviewed") {
    return <Check className="size-3.5 shrink-0 text-green-600" />;
  }
  // Pending — a small neutral bullet (not a selectable control).
  return (
    <div className="ml-1 size-1.5 shrink-0 rounded-full bg-muted-foreground/40" />
  );
}

function ReportStatusBadge({
  status,
  done,
  total,
}: {
  status: ReportStatus;
  done: number;
  total: number;
}) {
  if (status === "generating") {
    return (
      <Badge color="blue" size="sm" className="shrink-0">
        <Loader2 className="mr-1 size-3 animate-spin" />
        {done}/{total}
      </Badge>
    );
  }
  if (status === "failed") {
    return (
      <Badge color="red" size="sm" className="shrink-0">
        <AlertCircle className="mr-1 size-3" />
        Failed
      </Badge>
    );
  }
  if (status === "review") {
    return (
      <Badge color="blue" size="sm" className="shrink-0">
        <Sparkles className="mr-1 size-3" />
        Review
      </Badge>
    );
  }
  if (status === "complete") {
    return (
      <Badge color="green" size="sm" className="shrink-0">
        <Check className="mr-1 size-3" />
        Complete
      </Badge>
    );
  }
  return (
    <Badge color="gray" size="sm" className="shrink-0">
      Draft
    </Badge>
  );
}
